import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from '@mui/icons-material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShareIcon from '@mui/icons-material/Share';
import { Box, Checkbox, Grid, IconButton, Skeleton, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import { visuallyHidden } from '@mui/utils';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import CheckboxDot from '/@/components/checkbox-dot';
import Icon from '/@/components/icon';
import NoResults from '/@/components/my-recommendations/no-results';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { setRecommendation } from '/@/store/crop-monitoring/actions';
import { useMyRecommendationPDF } from '/@/views/dashboard/components/recommendations-list';
import { seedPdfVersion } from '/@/views/recommendations/seed-pdf-version';
import SharePopup from '../../share-popup';
import MyRecommendationsFilter from '../filter';
import styles from '../styles.module.css';

const headCells = [
    {
        id: 'type',
        label: 'Tipo',
    },
    {
        id: 'created',
        label: 'Creación',
    },
    {
        id: 'plantingDate',
        label: 'Fecha de siembra',
    },
    {
        id: 'fieldName',
        label: 'Nombre del lote',
    },
    {
        id: 'environment',
        label: 'Ambiente',
    },
    {
        id: 'product',
        label: 'Producto',
        sortable: false,
    },
] as const;

const expertoHeadCells = [
    {
        id: 'recommendation',
        label: 'Recomendación',
        sortable: false,
    },
    {
        id: 'field',
        label: 'Lote',
    },
    {
        id: 'created',
        label: 'Creación',
    },
    {
        id: 'plantingDate',
        label: 'Fecha de siembra',
    },
    {
        id: 'environment',
        label: 'Ambiente',
    },
    {
        id: 'product',
        label: 'Producto',
        sortable: false,
    },
] as const;

const headCellsForCropMonitoring = [
    {
        id: 'created',
        label: 'Creación',
    },
    {
        id: 'plantingDate',
        label: 'Fecha de siembra',
    },
    {
        id: 'environment',
        label: 'Ambiente',
    },
    {
        id: 'product',
        label: 'Producto',
        sortable: false,
    },
] as const;

function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, isCropMonitoring } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };
    const head = isCropMonitoring
        ? headCellsForCropMonitoring
        : settings.app.theme === 'expertobayer'
          ? expertoHeadCells
          : headCells;

    return (
        <TableHead>
            <TableRow>
                {!props.noDot && (
                    <TableCell padding='checkbox'>
                        {settings.app.theme === 'expertobayer' ? (
                            <Checkbox
                                sx={{ pl: 2 }}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={onSelectAllClick}
                            />
                        ) : (
                            <CheckboxDot
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={onSelectAllClick}
                                theme={settings.app.theme}
                            />
                        )}
                    </TableCell>
                )}

                {head.map((headCell) => (
                    <TableCell key={headCell.id} align='left' sortDirection={orderBy === headCell.id ? order : false}>
                        {headCell.sortable !== false ? (
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                                sx={{
                                    color: 'text.accent',
                                    '&.Mui-active': {
                                        color: 'text.accent',
                                    },
                                }}
                            >
                                {_(headCell.label)}
                                {orderBy === headCell.id ? (
                                    <Box component='span' sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        ) : (
                            <Box
                                sx={{
                                    color: 'text.accent',
                                }}
                            >
                                {_(headCell.label)}
                            </Box>
                        )}
                    </TableCell>
                ))}
                <TableCell align='left' />
                {settings.app.theme === 'expertobayer' && <TableCell align='left' />}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const MyRecommendationsTable = ({
    rows,
    setShowFilter,
    selected,
    setSelected,
    noDot = false,
    isCropMonitoring = false,
    handleNext,
    count,
    page,
    setPage,
    setSortBy,
    sortBy,
}) => {
    const rowsPerPage = 5;
    const history = useNavigate();
    const dispatch = useDispatch();

    const handleRequestSort = (event, property) => {
        const isAsc = sortBy === property;
        setSortBy(isAsc ? '-' + property : property);
    };

    const handleSelectAllClick = (event) => {
        let newSelecteds = [];
        if (event.target.checked) {
            newSelecteds = rows.map((n) => n.id);
        }
        setShowFilter && setShowFilter(newSelecteds.length === 0);
        setSelected(newSelecteds);
    };

    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setShowFilter && setShowFilter(newSelected.length === 0);
        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    const rowElement = useRef(null);

    const setRecoDispatch = bindActionCreators(setRecommendation, dispatch);

    const setReco = (id) => {
        setRecoDispatch(id);
        handleNext();
    };

    const handleRecoEditButton = (row) => {
        if (isCropMonitoring) {
            setReco(row.id);
        } else {
            row.onClick(row);
        }
    };

    return (
        <Fragment>
            {rows.length === 0 ? (
                <NoResults isCropMonitoring={isCropMonitoring} />
            ) : (
                <Box sx={{ mt: 2 }}>
                    <Paper
                        sx={{
                            width: '100%',
                            borderRadius: '8px',
                            boxShadow: 'none',
                            border: '1px solid #E1E1E1',
                        }}
                    >
                        {/* <EnhancedTableToolbar numSelected={selected.length} /> */}
                        <TableContainer>
                            <Table className={styles.table} aria-labelledby='tableTitle' size='medium'>
                                <EnhancedTableHead
                                    numSelected={selected.length}
                                    order={sortBy.startsWith('-') ? 'desc' : 'asc'}
                                    orderBy={sortBy.replace('-', '')}
                                    onSelectAllClick={handleSelectAllClick}
                                    onRequestSort={handleRequestSort}
                                    rowCount={rows.length}
                                    noDot={noDot}
                                    isCropMonitoring={isCropMonitoring}
                                />
                                <TableBody
                                    className='mp-mask'
                                    sx={{
                                        '& tr:last-child td, & tr:last-child th': { border: 0 },
                                    }}
                                >
                                    {rows.map((row, index) => {
                                        const isItemSelected = isSelected(row.id);
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow
                                                onClick={(event) => handleClick(event, row.id)}
                                                role='checkbox'
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                selected={isItemSelected}
                                                ref={rowElement}
                                                key={row.id}
                                            >
                                                {!noDot && (
                                                    <TableCell padding='checkbox'>
                                                        <CheckboxDot
                                                            checked={isItemSelected}
                                                            inputProps={{
                                                                'aria-labelledby': labelId,
                                                            }}
                                                            theme={settings.app.theme}
                                                        />
                                                    </TableCell>
                                                )}
                                                {!isCropMonitoring && (
                                                    <TableCell
                                                        align='left'
                                                        component='th'
                                                        id={labelId}
                                                        scope='row'
                                                        padding='none'
                                                    >
                                                        <div className={styles.icon}>
                                                            <Icon
                                                                type={row.icon}
                                                                size='md'
                                                                theme={settings.app.theme}
                                                            />
                                                            {row.iconLabel}
                                                        </div>
                                                    </TableCell>
                                                )}

                                                <TableCell align='left'>{row.created}</TableCell>
                                                <TableCell align='left'>{row.plantingDate}</TableCell>
                                                {!isCropMonitoring && (
                                                    <TableCell align='left'>{row.fieldName}</TableCell>
                                                )}

                                                <TableCell align='left'>{row.environment}</TableCell>
                                                <TableCell align='left'>
                                                    {row.product.map((product) => (
                                                        <div key={product}>{product}</div>
                                                    ))}
                                                </TableCell>

                                                <Tooltip
                                                    title={
                                                        row.isOldRecommendation && !isCropMonitoring
                                                            ? _(
                                                                  'Esta recomendación fue creada en una campaña anterior, y los datos podrían estar desactualizados. En el caso de querer información más reciente, te aconsejamos crear una nueva.'
                                                              )
                                                            : ''
                                                    }
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                marginTop: '-15px !important',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <TableCell align='left'>
                                                        <Button
                                                            variant={isCropMonitoring ? 'contained' : 'outlined'}
                                                            onClick={() => handleRecoEditButton(row)}
                                                            endIcon={
                                                                row.isOldRecommendation &&
                                                                !isCropMonitoring && <ErrorOutlineIcon color='error' />
                                                            }
                                                        >
                                                            {isCropMonitoring ? _('Seleccionar') : _('Ver')}
                                                        </Button>
                                                    </TableCell>
                                                </Tooltip>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                    <Pagination
                        size='small'
                        shape='rounded'
                        count={Math.ceil(count / rowsPerPage)}
                        defaultPage={0}
                        page={page}
                        onChange={(ev, page) => handleChangePage(ev, page)}
                        color='primary'
                        sx={{
                            p: 3,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    />
                </Box>
            )}
        </Fragment>
    );
};

export default MyRecommendationsTable;

export const MyRecommendationsExpertoTable = ({
    rows,
    setShowFilter,
    selected,
    setSelected,
    noDot = false,
    isCropMonitoring = false,
    handleNext,
    count,
    page,
    setPage,
    setSortBy,
    sortBy,
    filterOptions,
    setFilterRecos,
    filterRecos,
    loading,
    rowsPerPage,
    setRowsPerPage,
    shareRecommendations,
}) => {
    const dispatch = useDispatch();
    const [searchInput, setSearchInput] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const [selectedRow, setselectedRow] = useState(undefined);
    const [shareRecommendation, setShareRecommendation] = useState(undefined);
    useEffect(() => {
        setFilterRecos({ ...filterRecos, search: searchInput });
    }, [searchInput]);

    const handleInputFilter = (e) => {
        setSearchInput(e.target.value);
    };

    const handleRequestSort = (event, property) => {
        const isAsc = sortBy === property;
        setSortBy(isAsc ? '-' + property : property);
    };

    const handleSelectAllClick = (event) => {
        let newSelecteds = [];
        if (event.target.checked) {
            newSelecteds = rows.map((n) => n.id);
        }
        setShowFilter && setShowFilter(newSelecteds.length === 0);
        setSelected(newSelecteds);
    };
    //TODO: IMPROVE
    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setShowFilter && setShowFilter(newSelected.length === 0);
        setSelected(newSelected);
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    const rowElement = useRef(null);

    const setRecoDispatch = bindActionCreators(setRecommendation, dispatch);

    const setReco = (id) => {
        setRecoDispatch(id);
        handleNext();
    };

    const handleRecoEditButton = (row) => {
        if (isCropMonitoring) {
            setReco(row.id);
        } else {
            row.onClick(row);
        }
    };
    const recommendation = useMemo(
        () => shareRecommendations.find((reco) => reco.id === shareRecommendation?.id),
        [shareRecommendation, shareRecommendations]
    );
    const { handleEmail, handleWhatsApp, handleDownload } = useMyRecommendationPDF(recommendation || undefined);
    // Only the v2 template has selectable sections, so it's the only one whose popup opens on
    // the block picker — everything else keeps the flow it has today.
    const selectedPdfVersion = seedPdfVersion(recommendation);

    return (
        <Box sx={{ mx: 8, my: 4, border: '1px solid lightgray', borderRadius: 2 }}>
            <Grid container spacing={2} direction={'column'} sx={{ pt: 4, px: 4 }}>
                <Grid item>
                    <Typography color='primary' variant='h6'>
                        {_('Listado de recomendaciones')}
                    </Typography>
                </Grid>
                <Grid item container spacing={1}>
                    <Grid item xs={12} lg={4}>
                        <TextField
                            value={searchInput}
                            onChange={handleInputFilter}
                            onReset={() => setSearchInput('')}
                            label={_('Buscar por lote, tipo, fecha, etc.')}
                            size='small'
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <MyRecommendationsFilter
                            fields={['type', 'field', 'plantingDate']}
                            onChangeFilters={setFilterRecos}
                            filters={filterRecos}
                            filterOptions={filterOptions}
                            setSearchInput={setSearchInput}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {loading ? (
                <Grid container flexDirection='column' spacing={2} sx={{ my: 2 }}>
                    {Array.from(new Array(rowsPerPage)).map((_, index) => (
                        <Grid item key={index}>
                            <Skeleton variant='rectangular' width='100%' height={70} />
                        </Grid>
                    ))}
                </Grid>
            ) : rows.length === 0 ? (
                <NoResults isCropMonitoring={isCropMonitoring} />
            ) : (
                <Box sx={{ mt: 2, px: 4 }}>
                    <Paper sx={{ width: '100%', boxShadow: 'none' }}>
                        <TableContainer>
                            <Table className={styles.table} aria-labelledby='tableTitle' size='medium'>
                                <EnhancedTableHead
                                    numSelected={selected.length}
                                    order={sortBy.startsWith('-') ? 'desc' : 'asc'}
                                    orderBy={sortBy.replace('-', '')}
                                    onSelectAllClick={handleSelectAllClick}
                                    onRequestSort={handleRequestSort}
                                    rowCount={rows.length}
                                    noDot={noDot}
                                    isCropMonitoring={isCropMonitoring}
                                />
                                <TableBody
                                    className='mp-mask'
                                    sx={{ '& tr:last-child td, & tr:last-child th': { border: 0 } }}
                                >
                                    {rows.map((row, index) => {
                                        const isItemSelected = isSelected(row.id);
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow
                                                onClick={(event) => handleClick(event, row.id)}
                                                role='checkbox'
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                selected={isItemSelected}
                                                ref={rowElement}
                                                key={row.id}
                                            >
                                                {!noDot && (
                                                    <TableCell padding='checkbox'>
                                                        <Checkbox
                                                            sx={{ pl: 2 }}
                                                            checked={isItemSelected}
                                                            inputProps={{
                                                                'aria-labelledby': labelId,
                                                            }}
                                                        />
                                                    </TableCell>
                                                )}
                                                {!isCropMonitoring && (
                                                    <TableCell
                                                        align='left'
                                                        component='th'
                                                        id={labelId}
                                                        scope='row'
                                                        padding='none'
                                                    >
                                                        <div className={styles.icon}>
                                                            <Icon
                                                                type={row.icon}
                                                                size='md'
                                                                theme={settings.app.theme}
                                                            />
                                                            {row.iconLabel}
                                                        </div>
                                                    </TableCell>
                                                )}

                                                {!isCropMonitoring && (
                                                    <TableCell align='left'>{row.fieldName}</TableCell>
                                                )}
                                                <TableCell align='left'>{row.created}</TableCell>
                                                <TableCell align='left'>{row.plantingDate}</TableCell>

                                                <TableCell align='left'>{row.environment || '-'}</TableCell>
                                                <TableCell align='left'>
                                                    {row.product.map((product) => (
                                                        <div key={product}>{product}</div>
                                                    ))}
                                                </TableCell>

                                                {row.hasShare ? (
                                                    <Tooltip
                                                        title={
                                                            row.isOldRecommendation && !isCropMonitoring
                                                                ? _(
                                                                      'Esta recomendación fue creada en una campaña anterior, y los datos podrían estar desactualizados. En el caso de querer información más reciente, te aconsejamos crear una nueva.'
                                                                  )
                                                                : ''
                                                        }
                                                        arrow
                                                        componentsProps={{
                                                            tooltip: {
                                                                sx: {
                                                                    marginTop: '-15px !important',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <TableCell align='left'>
                                                            <ShareIcon
                                                                sx={{ cursor: 'pointer' }}
                                                                color='secondary'
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowPopup(true);
                                                                    setselectedRow(row);
                                                                    setShareRecommendation(row);
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </Tooltip>
                                                ) : (
                                                    <TableCell />
                                                )}
                                                <TableCell align='left'>
                                                    <ArrowForwardIosIcon
                                                        sx={{ cursor: 'pointer' }}
                                                        color='secondary'
                                                        onClick={() => handleRecoEditButton(row)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {/* `recommendation` is the saved recommendation, not the
                                        table's processed row: the popup hands it straight to the
                                        PDF hook, which reads `crops`/`weeds` off the original. */}
                                    <SharePopup
                                        type={selectedRow?.type}
                                        visible={showPopup}
                                        title={_('Compartir tu recomendación')}
                                        whatsAppIntro={_('Te envío una recomendación generada en {{ appName }}', {
                                            appName: settings.app.name,
                                        })}
                                        onClose={() => {
                                            setShowPopup(false);
                                            setselectedRow(undefined);
                                        }}
                                        onEmail={handleEmail}
                                        onWhatsApp={handleWhatsApp}
                                        onDownload={handleDownload}
                                        recommendation={recommendation}
                                        version={2}
                                        entrySource='my-recommendations'
                                        pdfVersion={selectedPdfVersion}
                                        mode='share'
                                    />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Grid
                        container
                        spacing={2}
                        justifyContent={'end'}
                        alignItems={'center'}
                        sx={{ my: 2 }}
                        flexWrap={'nowrap'}
                    >
                        <Grid item>{_('Filas por página:')}</Grid>
                        <Grid item>
                            <MuiSelect
                                formControlProps={{ sx: { width: 60 } }}
                                inputLabel={_('Filas')}
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(e.target.value);
                                }}
                                size='small'
                                options={[5, 10, 20].map((o) => {
                                    return { label: o, value: o };
                                })}
                            />
                        </Grid>
                        <Grid item>
                            <Typography>
                                {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                                    (page - 1) * rowsPerPage + 1 + rowsPerPage - 1,
                                    count
                                )} de ${count}`}
                            </Typography>
                        </Grid>
                        <Grid item container spacing={1} alignItems={'center'} sx={{ width: 'auto' }}>
                            <Grid item>
                                <IconButton onClick={() => setPage(1)} disabled={page === 1}>
                                    <FirstPage sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <IconButton
                                    onClick={() => setPage((page) => Math.max(1, page - 1))}
                                    disabled={page === 1}
                                >
                                    <KeyboardArrowLeft sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <IconButton
                                    onClick={() =>
                                        setPage((page) => Math.min(page + 1, Math.ceil(count / rowsPerPage)))
                                    }
                                    disabled={page === Math.ceil(count / rowsPerPage)}
                                >
                                    <KeyboardArrowRight sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <IconButton
                                    onClick={() => setPage(Math.ceil(count / rowsPerPage))}
                                    disabled={page === Math.ceil(count / rowsPerPage)}
                                >
                                    <LastPage sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Box>
    );
};
